import { safeLocalStorage } from '@toss/storage';
import { useRouter } from 'next/router';

import { logger } from '@zep/core-logger';

import { SESSION_KEY } from '@/hooks/useDuplicateLoginDetector';
import { useUser } from '@/lib/auth';
import { resolvePostLoginDestination } from '@/module/auth/login/utils/resolvePostLoginDestination';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { UserCookieManager } from '@/shared/cookies';
import { useUnknownErrorDialog } from '@/shared/hooks';

const QUIZ_ADMIN_PROXY_PREFIX = '/admin';

function shouldUseHardRedirectAfterLogin(redirectUrl: string) {
  return (
    redirectUrl === QUIZ_ADMIN_PROXY_PREFIX ||
    redirectUrl.startsWith(`${QUIZ_ADMIN_PROXY_PREFIX}/`)
  );
}

/** success_code_verify 의 destination 판정: URL 패턴으로 분류. */
export function classifyDestination(
  url: string,
): 'signup' | 'quizzes' | 'classrooms' {
  if (url.startsWith('/sign-up')) return 'signup';
  if (url.includes('/quizzes')) return 'quizzes';
  return 'classrooms';
}

export type OnResolvedInfo = {
  isAccountSwitch: boolean;
  destination: 'quizzes' | 'classrooms' | 'signup';
};

export const useOnSuccessLogin = () => {
  const router = useRouter();
  const unknownErrorDialog = useUnknownErrorDialog();
  const { syncLatestUserInfo, persistSwitchedUserToStorage } = useUser();

  return async ({
    zepServerToken,
    userSessionKey,
    callbackUrl,
    onResolved,
  }: {
    zepServerToken: string;
    userSessionKey: string;
    callbackUrl: string;
    onResolved?: (info: OnResolvedInfo) => void;
  }) => {
    try {
      // 이미 다른 계정으로 로그인된 상태에서 새 계정으로 로그인(예: A 로그인 중 B 진입 코드/QR)하면
      // 토큰·user 는 B 로 바뀌지만 React Query 캐시엔 A 데이터가 남아, SPA 전환 시 도착지(/myquiz,
      // /classrooms/*)가 섞인 상태로 렌더되며 setState 루프(React #185)가 난다. 이때는 hard redirect 로
      // 새 계정을 풀로드해 stale 캐시를 비운다.
      // 판별은 localStorage 토큰을 동기적으로 읽어 비교한다(useUser 의 user 는 code-login 딥링크에서
      // 직전 계정이 컨텍스트에 하이드레이트되기 전일 수 있어 racy — 토큰 읽기는 그 타이밍과 무관).
      const previousToken = ApiTokenStorage.get();
      const isAccountSwitch =
        !!previousToken && previousToken !== zepServerToken;

      ApiTokenStorage.set(zepServerToken);

      safeLocalStorage.set(SESSION_KEY, userSessionKey); // 중복접속 방지를 위한 세션 키 저장
      UserCookieManager.setSession();

      // 계정 전환(이미 A 로 로그인된 상태에서 B 진입)이면 풀로드로 새 계정을 깨끗이 띄운다.
      // 단 토큰만 B 로 바꾸고 풀로드하면 user('quiz_user' localStorage)는 A 로 남아, 이 앱은
      // isAuthenticated = !!user 라 새 페이지가 A 로 부팅돼 헤더 아바타 등이 stale 하다.
      // syncLatestUserInfo 는 setUser(setState)를 호출해 SubscribeUserPlan #185 루프를 유발하므로,
      // setState 없이 localStorage 에만 B user 를 기록하는 persistSwitchedUserToStorage 로
      // user 를 B 로 맞춘 뒤 풀로드한다 → 새 페이지가 B 로 부팅(토큰·user 일관, stale 없음).
      if (isAccountSwitch) {
        const switchedUser = await persistSwitchedUserToStorage(zepServerToken);
        // 프로필 조회가 조용히 실패(isAuthProfileIgnoredError → data 없음)하면 토큰만 B 로 바뀌고
        // localStorage user 는 이전 계정(A)에 멈춰 "반쪽 로그인"이 된다. 또 registrationStatus 가
        // undefined 라 가입완료 학생도 가입화면으로 오라우팅된다. 진행하지 말고 catch 로 넘긴다.
        if (!switchedUser) {
          throw new Error('account-switch-profile-fetch-failed');
        }
        // 일반 경로와 동일한 도착지 규칙 적용(미가입→가입화면, role 기본 폴백). 다른 점은 풀로드 뿐.
        const switchedRedirectUrl = resolvePostLoginDestination({
          registrationStatus: switchedUser.registrationStatus,
          userType: switchedUser.type,
          callbackUrl,
          query: router.query,
        });
        onResolved?.({
          isAccountSwitch: true,
          destination: classifyDestination(switchedRedirectUrl),
        });
        window.location.replace(switchedRedirectUrl);
        return;
      }

      // 로그인 성공 시, 유저 정보를 서버와 동기화합니다.
      const latestUserInfo = await syncLatestUserInfo(zepServerToken);

      const redirectUrl = resolvePostLoginDestination({
        registrationStatus: latestUserInfo.user.registrationStatus,
        userType: latestUserInfo.user.memberType,
        callbackUrl,
        query: router.query,
      });

      onResolved?.({
        isAccountSwitch: false,
        destination: classifyDestination(redirectUrl),
      });

      if (shouldUseHardRedirectAfterLogin(redirectUrl)) {
        window.location.replace(redirectUrl);
        return;
      }

      await router.replace(redirectUrl);
    } catch (error) {
      logger.captureError(error, {
        fingerprint: 'auth-error',
      });
      await unknownErrorDialog.open();
      void router.replace({ pathname: '/login', query: { callbackUrl } });
      return;
    }
  };
};
